import { forwardRef, useImperativeHandle, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../../AuthContext';
import { ALL_INVENTORY_ITEMS_QUERY } from '../../graphql/inventory';
import { ObjectTypeFormHandle, ObjectTypeFormProps } from './types';
import InventorySearchSelect, { InventoryOption } from './InventorySearchSelect';

// Reupholster Pop Seat — per Quoting_markdown/reupholster_dining_chairs.md.
// A small drop-in seat cushion (e.g. a kitchen chair seat), fully
// self-contained: no back, no skirt, no delivery charge, and a "two seats
// per cut" fabric-sharing rule driven by the object's own batch quantity.
// Submitted through dedicated addReupholsterPopSeatDraftQuoteObject/
// updateReupholsterPopSeatDraftQuoteObject mutations, same deviation
// Bench/Cornice/Bed/Reupholster Bench/Ottoman already established.
const ReupholsterPopSeatForm = forwardRef<ObjectTypeFormHandle, ObjectTypeFormProps>((props, ref) => {
  const { user } = useAuth();
  const iv = props.initialValues;

  const [width, setWidth] = useState(iv?.widthInches != null ? String(iv.widthInches) : '');
  const [depth, setDepth] = useState(iv?.depthInches != null ? String(iv.depthInches) : '');
  const [fabricDescription, setFabricDescription] = useState<string>(iv?.fabricDescription || '');
  const [fabricWidth, setFabricWidth] = useState(iv?.fabricWidthInches != null ? String(iv.fabricWidthInches) : '54');
  const [patternRepeat, setPatternRepeat] = useState(iv?.patternRepeatInches != null ? String(iv.patternRepeatInches) : '');
  const [hrFoamDacron, setHrFoamDacron] = useState<boolean>(iv?.hrFoamDacron || false);
  const [weltBottom, setWeltBottom] = useState<boolean>(iv?.weltBottom || false);
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id,
  });
  const fabricItems: InventoryOption[] = (data?.allInventoryItems?.nodes || [])
    .filter((i: any) => !i.archivedAt && i.inventoryType === 'FABRIC')
    .map((i: any) => ({ id: i.id, name: i.name, costCents: i.costCents ?? 0, widthInches: i.widthInches ?? null, inventoryType: i.inventoryType }));

  useImperativeHandle(ref, () => ({
    getValues: () => {
      if (!width) { setError('Width is required'); return null; }
      if (!depth) { setError('Depth is required'); return null; }
      setError(null);
      return {
        widthInches: parseFloat(width),
        depthInches: parseFloat(depth),
        fabricDescription: fabricDescription.trim() || null,
        fabricWidthInches: fabricWidth ? parseFloat(fabricWidth) : null,
        patternRepeatInches: patternRepeat ? parseFloat(patternRepeat) : null,
        hrFoamDacron,
        weltBottom,
      };
    },
  }));

  return (
    <div>
      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Width (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={width}
            onChange={(e) => setWidth(e.target.value)} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Depth (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={depth}
            onChange={(e) => setDepth(e.target.value)} disabled={props.disabled} />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <InventorySearchSelect
            items={fabricItems}
            value={fabricDescription}
            onChange={(name) => setFabricDescription(name)}
            placeholder="Search inventory or type fabric description..."
            disabled={props.disabled}
          />
        </div>
        <div className="form-group" style={{ width: 120 }}>
          <label>Fabric Width (in)</label>
          <input type="number" min="0" step="1" className="input" value={fabricWidth}
            onChange={(e) => setFabricWidth(e.target.value)} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ width: 140 }}>
          <label>Pattern Repeat (in) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <input type="number" min="0" step="0.25" className="input" value={patternRepeat}
            onChange={(e) => setPatternRepeat(e.target.value)} disabled={props.disabled} />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={hrFoamDacron}
              onChange={(e) => setHrFoamDacron(e.target.checked)} disabled={props.disabled} />
            {' '}With new HR foam and dacron wrap (+$40 each)
          </label>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={weltBottom}
              onChange={(e) => setWeltBottom(e.target.checked)} disabled={props.disabled} />
            {' '}With self welt around bottom ($36/yd by perimeter)
          </label>
        </div>
      </div>

      {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
    </div>
  );
});

export default ReupholsterPopSeatForm;
